import React from 'react';
import { 
  Box, 
  IconButton, 
  Menu, 
  MenuItem, 
  Typography,
  Tooltip,
  Button,
  Badge
} from '@mui/material';
import { LanguageOutlined as LanguageIcon } from '@mui/icons-material';
import useLanguage from '../../hooks/useLanguage';
import useAuth from '../../hooks/useAuth';

/**
 * Language Switcher Component
 * Allows users to change the application language
 */
const LanguageSwitcher = () => {
  const { currentLanguage, availableLanguages, changeLanguage, t } = useLanguage();
  const { user, isAuthenticated, updateProfile } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLanguageChange = async (languageCode) => {
    // Close the menu
    handleMenuClose();
    
    // Change the language
    await changeLanguage(languageCode);
    
    // If user is authenticated, update their preferences
    if (isAuthenticated && user) {
      try {
        await updateProfile({
          settings: {
            ...user.settings,
            language: languageCode
          }
        });
      } catch (error) {
        console.error('Failed to update language preference', error);
      }
    }
  };
  
  return (
    <Box>
      <Button
        onClick={handleMenuOpen}
        endIcon={<span style={{ fontSize: '8px', color: 'white' }}>▼</span>}
        sx={{
          minWidth: 'auto',
          backgroundColor: 'transparent',
          color: 'white',
          fontWeight: 'medium',
          padding: '4px 8px',
          '&:hover': {
            backgroundColor: 'transparent',
          }
        }}
      >
        {currentLanguage.toUpperCase()}
      </Button>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        keepMounted
        PaperProps={{
          sx: {
            minWidth: '80px',
            mt: 1
          }
        }}
      >
        {availableLanguages.map((language) => (
          <MenuItem 
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={currentLanguage === language.code}
            sx={{
              justifyContent: 'center',
              py: 1
            }}
          >
            <Typography variant="body2" fontWeight={currentLanguage === language.code ? 'bold' : 'normal'}>
              {language.code.toUpperCase()}
            </Typography>
          </MenuItem>
        ))}
        
        {isAuthenticated && user?.systemRole === 'admin' && (
          <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.12)', mt: 1, pt: 1 }}>
            <MenuItem
              onClick={() => {
                handleMenuClose();
                window.location.href = '/settings/translations';
              }}
            >
              <Typography variant="body2" color="primary">
                {t('settings.language')} {t('settings.title')}
              </Typography>
            </MenuItem>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default LanguageSwitcher;